import AuthManagerBase from "@lib/apitools/AuthManagerBase";
import ProviderBase from "@lib/apitools/ProviderBase";
import {
    AssetData, AssetExcursion, AssetLinesData, Last, LastData, Sources,
    SourcesData, Stats, StatsValues
} from "src/lib/types/live-api";
import { Asset } from "src/lib/types/objects";

const dateFormat = (date: Date) =>
    date.toISOString()
        .split(".")[0]
        .replace("T", " ");


type AppInfo = {
    name: string
    version: string
}

type TimeDifference = {
    server_time_ns: number,
    time_diff: number
}

type AppConfig = object

const q = (dict: object, sep = "."): string[] =>
    Object.entries(dict)
        .reduce(
            (arr, [k, v]) => {
                let vals: string[] = [];
                if(Array.isArray(v)) {
                    vals =  v.filter(s => s !== undefined && s !== null && s !== '')
                        .map(s => `${k}=${encodeURIComponent(s)}`);
                } else if (v && typeof v === 'object') {
                    vals = q(v).map(d => `${k}${sep}${d}`);
                } else if(v) {
                    vals = [`${k}=${encodeURIComponent(v)}`];
                }
                return arr.concat(vals);
            },
            [] as string[]
        );


type Query = {
    [key in string]: string | number | boolean | string[] | number[] | undefined
}

class LiveAPI extends ProviderBase {
    constructor(apiUrl: string, authManager: AuthManagerBase) {
        super(apiUrl, authManager);
        this.qSep = "_";
    }
    query(dict: object): string {
        return "?" + q(dict, this.qSep).join("&");
    }
    timezoneHeader(): {Timezone: string} {
        return {Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}
    }
    async getServerTime(currentTimeNs: number): Promise<TimeDifference> {
        return this.reqWithAuth<TimeDifference>(`time-diff/${currentTimeNs}`);
    }
    // /live
    async getCombined(
        req: {last?: Last, data?: Sources, stats?: Stats},      
        period: number,
        offset: number = 0,
        start: number | null = null,
        end: number | null = null
    ) {
        const query = this.query({
            period: period || undefined, offset, start, end
        });
        return this.postJsonWithAuth<StatsValues>(`data` + query, req);
    }
    async getLast(sources: Last): Promise<LastData> {
        return this.postJsonWithAuth<LastData>(`data/last`, sources);
    }
    async getStats(
        stats: Stats,
        period: number,
        offset: number = 0,
        start: number | null = null,
        end: number | null = null
    ): Promise<StatsValues> {
        const query = this.query({
            period: period || undefined, offset, start, end
        });
        return this.postJsonWithAuth<StatsValues>(`data/stats` + query, stats);
    }
    async getData(
        sources: Sources,
        period: number,
        offset: number = 0,
        start: number | null = null,
        end: number | null = null
    ): Promise<SourcesData> {
        const query = this.query({
            period: period || undefined, offset, start, end
        });
        return this.postJsonWithAuth<SourcesData>(`data/data` + query, sources);
    }
    async getHistogram(
        sources: Sources,
        period: number,
        offset: number = 0,
        start: number | null = null,
        end: number | null = null
    ): Promise<SourcesData> {
        const query = this.query({
            period: period || undefined, offset, start, end
        });
        return this.postJsonWithAuth(`data/histogram` + query, sources);
    }
    downloadData({ext, ...params}: {
        ext: "csv" | "xlsx" | "db",
        tables: string[]
        periods: [number, number][]
        columns: {[key: string]: string[]}
    }) {
        return this.fetchFile(`data/export/${ext}`, {
            method: "POST",
            headers: {
                'Content-Type': "application/json"
            },
            body: JSON.stringify(params)
        });
    }
    

    //gps
    loadMapData(
        query: object = {}
    ) {
        return this.reqWithAuth<T[]>(`map` + this.query(query));
    }

    uploadFile(type: string, file: File): Promise<{src: string, type: string}> {
        const fd = new FormData();
        fd.append("img", file);
        return this.reqWithAuth("files/image/" + type, {
            method: "PUT",
            body: fd
        });
    }

    // assets
    getAssets() {
        return this.reqWithAuth<Asset[]>("assets");
    }
    getAssetMap(query: Query) {
        return this.reqWithAuth("assets/map" + this.query(query));
    }
    downloadAssetsReport(asset: number, query: Query) {
        return this.reqFileWithAuth(`assets/${asset}/report`
                                    + this.query(query), {headers: this.timezoneHeader()});
    }
    getForecastAnalysis(asset:number, query: Query) {
        return  this.reqWithAuth(`assets/${asset}/forecast` + this.query(query));
    }
    getAllLinesLastData(query = {}) {
        return this.reqWithAuth<AssetLinesData>(`assets/lines` + this.query(query));
    }
    updateLineData(asset: number, line: number, data: object) {
        return this.reqWithAuth(`assets/${asset}/lines/${line}`, {
            method: "PATCH",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(data)
        });
    }
    getUpdates(type: string, query: Query) {
        if(type == "line")
            return this.reqWithAuth<{
                items: {
                    data: object,
                    id: number,
                    time: number,
                    update_type: string
                }[],
                total: number
            }>(`updates/line` + this.query(query));
        throw new Error("Not implemented for type " + type);
    }

    updateCrane(asset: number, data: object) {
        return this.reqWithAuth(`assets/${asset}/crane`, {
            method: "PATCH",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(data)
        });
    }

    getDeviceInfo(url: string) {
        return this.reqWithAuth(`map/device-info` + this.query({url}));
    }

    // events
    getAssetAlarms(asset: number, query: Query) {
        return this.reqWithAuth(`assets/${asset}/alerts`
            + this.query(query));
    }
    downloadAssetAlarms(asset: number, query: Query) {
        return this.reqFileWithAuth(`assets/${asset}/alerts/download`
            + this.query(query), {headers: this.timezoneHeader()});
    }
    getAssetAlarmsCount(asset: number, query: Query) {
        return this.reqWithAuth(`assets/${asset}/alerts/count`
            + this.query(query));
    }
    getAlarms(query: Query) {
        return this.reqWithAuth(`alerts`
            + this.query(query));
    }
    getAlarmsFilters() {
        return this.reqWithAuth("alerts/filters");
    }
    getAlarmsCount(query = {}) {
        console.log("ALARMS COUNT QUERY", query)
        return this.reqWithAuth(`alerts/count`
            + this.query(query));
    }
    downloadAlarms(query: Query) {
        return this.reqFileWithAuth(`alerts/download`
            + this.query(query), {headers: this.timezoneHeader()});
    }
    updateAlarm(
        alertGroupId: string, update: object
    ) {
        return this.postJsonWithAuth(
            `alerts/alert_group/${alertGroupId}`, update
        );
    }

    updateAlarms(
        alert_group_ids: string[],
        data: {state: string, comments: string}
    ) {
        return this.postJsonWithAuth(
            `alerts/alert_group/batch-update`, {alert_group_ids, data}
        );
    }

    getStatus() {
        return this.reqWithAuth("status");
    }

    //camera
    getCameraList() {
        return this.reqWithAuth("camera");
    }
    getCameraFilesCount(camera: string) {
        return this.reqWithAuth("camera/" + camera);
    }
    getCameraFilesForDate(camera: string, date: string) {
        return this.reqWithAuth(`camera/${camera}/${date}`);
    }

    // admin

    getUsers(query: Query) {
        return this.reqWithAuth("users" + this.query(query));
    }
    getUser(username: string) {
        return this.reqWithAuth("users/" + username);
    }
    createUser(userData: object) {
        return this.reqWithAuth("users", {
            body: JSON.stringify(userData),
            method: "POST",
            headers: {
                'Content-Type': "application/json"
            }
        });
    }
    updateUser(id: string, userData: object) {
        return this.reqWithAuth("users/" + id, {
            body: JSON.stringify(userData),
            method: "PUT",
            headers: {
                'Content-Type': "application/json"
            }
        });
    }
    deleteUser(id: string) {
        return this.reqWithAuth("users/" + id, {
            method: "DELETE",
        });
    }
    resetUserPassword(id: string) {
        return this.reqWithAuth("users/" + id + "/reset-password", {
            method: "PUT",
        });
    }
    getModel(id: number) {
        return this.reqFileWithAuth(`model/${id}/vtp`);
    }
    getDamageStats(asset_id: number, query: object) {
        return this.reqWithAuth(`analysis/${asset_id}/damage-stats`
            + this.query(query));
    }
    getDamageAnalysis(asset_id: number, query: object) {
        return this.reqWithAuth(`analysis/${asset_id}/damage` + this.query(query), {headers: this.timezoneHeader()});
    }
    downloadDamageAnalysis(asset_id: number, query: object) {
        return this.reqFileWithAuth(
            `analysis/${asset_id}/damage` + this.query({...query, download: 1},), {headers: this.timezoneHeader()}
        );
    }
    getDamageYearly(asset_id: number, query: object) {
        return this.reqWithAuth(`analysis/${asset_id}/damage-yearly` + this.query(query));
    }
    downloadDamageYearly(asset_id: number, query: object) {
        return this.reqFileWithAuth(
            `analysis/${asset_id}/damage-yearly` + this.query({
                ...query,
                download: 1
            }), {headers: this.timezoneHeader()});
    }
    getStresses(asset_id: number, query: object) {
        return this.reqWithAuth(`analysis/${asset_id}/stresses` + this.query(query));
    }
    getFormattedData(params, start, end) {
        return this.postJsonWithAuth(
            "data_extra/data" + this.query({start, end}),
            params
        )
    }
    downloadFormattedData(params, start, end, timestep, ext) {
        return this.reqFileWithAuth(
            "data_extra/data/download" + this.query({start, end, timestep, ext}), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...this.timezoneHeader()
                },
                body: JSON.stringify(params)
            }
        )
    }
    getAssetsFull() {
        return this.reqWithAuth("dev-tools/assets");
    }
    validateAsset(asset) {
         return this.postJsonWithAuth(`dev-tools/assets/${asset.id}/validate`, asset);
    }
    updateAsset(asset) {
         return this.putJsonWithAuth(`dev-tools/assets/${asset.id}`, asset);
    }
    calculatePolyester(assetId, lineId, data) {
        return this.postJsonWithAuth(`dev-tools/moordyn/predict/${assetId}/lines/${lineId}`, data);
    }

    getGPSQuality(sensorId: number, start?: number, end?: number) {
        return this.reqWithAuth("data_extra/gps-quality" + this.query({sensor_id: sensorId, start, end}))
    }

    getTransmissionRates(start?: number, end?: number) {
        return this.reqWithAuth("data_extra/transmission-rates" + this.query({start, end}))
    }
}

const liveAPI = new LiveAPI("", new AuthManagerBase);
export default liveAPI;